import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type { Column, FilterOption } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { DeleteReasonDialog } from "@/components/shared/delete-reason-dialog";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useCompleteExpense, useDeleteExpense } from "@/hooks/use-expenses";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { EntityActivityLogDialog } from "@/pages/activity-logs/entity-activity-log-dialog";
import { expenseService } from "@/services/expense";

import { CostImpactDialog } from "./cost-impact-dialog";
import { expenseColumns } from "./expense-columns";
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_STATUS_LABELS,
} from "./expense-labels";
import { ExpenseRowActions } from "./expense-row-actions";
import { ExpensePdfButton } from "./pdf/expense-pdf-button";

import type { Expense, ExpenseCostImpactPreview } from "@artisancode/api-types";

const categoryFilters: FilterOption[] = [
  {
    key: "status",
    label: "Status",
    options: Object.entries(EXPENSE_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    key: "category",
    label: "Kategori",
    options: Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  },
];

export function ExpenseList() {
  const canCreate = useHasPermission("expenses.create");
  const canUpdate = useHasPermission("expenses.update");
  const canDelete = useHasPermission("expenses.delete");
  const canDeletePosted = useHasPermission("expenses.delete_posted");
  const canApprove = useHasPermission("expenses.approve");
  const { mutate: completeExpense } = useCompleteExpense();

  const { mutate: deleteExpense, isPending: isDeleting } = useDeleteExpense();

  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [completing, setCompleting] = useState<Expense | null>(null);
  const [historyExpenseId, setHistoryExpenseId] = useState<string | null>(null);

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const columns: Column<Expense>[] = [
    ...expenseColumns.slice(0, 2),
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (e: Expense) => (
              <span className="text-sm">{workspaceName(e.workspaceId)}</span>
            ),
          },
        ]
      : []),
    ...expenseColumns.slice(2),
  ];

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<Expense>({
    queryKey: (params) => queryKeys.expenses.list(params),
    fetcher: (params) => expenseService.list(params),
    pageSize: 10,
    filterKeys: ["category", "status", "date_from", "date_to", "workspace_id"],
  });

  function handleDeleteConfirm(reason: string) {
    if (!deleting) return;
    deleteExpense(
      { id: deleting.id, reason },
      {
        onSuccess: () => {
          toast.success("Pengeluaran berhasil dihapus.");
          setDeleting(null);
        },
        onError: () => toast.error("Gagal menghapus pengeluaran."),
      },
    );
  }

  const [pendingImpact, setPendingImpact] = useState<{
    expenseId: string;
    impact: ExpenseCostImpactPreview;
  } | null>(null);
  const [isCheckingImpact, setIsCheckingImpact] = useState(false);

  async function handleComplete(item: Expense) {
    setIsCheckingImpact(true);
    try {
      const preview = await expenseService.previewDraftCost(item.id);
      if (preview.hasImpact) {
        setPendingImpact({ expenseId: item.id, impact: preview });
        setIsCheckingImpact(false);
        return;
      }
    } catch {
      // If preview fails, fall through to complete which handles 409
    }
    setIsCheckingImpact(false);
    executeComplete(item.id, false);
  }

  function executeComplete(id: string, acknowledgeCostImpact: boolean) {
    completeExpense(
      { id, acknowledgeCostImpact },
      {
        onSuccess: () => {
          toast.success("Pengeluaran diselesaikan.");
          setPendingImpact(null);
        },
        onError: (err) => {
          const appErr = err as {
            data?: ExpenseCostImpactPreview;
            code?: string;
            message?: string;
          };
          if (appErr?.data?.hasImpact) {
            setPendingImpact({ expenseId: id, impact: appErr.data });
            return;
          }
          toast.error(
            err instanceof Error
              ? err.message
              : "Gagal menyelesaikan pengeluaran.",
          );
        },
      },
    );
  }

  function openAdd() {
    navigate("/expenses/new");
  }

  function openEdit(item: Expense) {
    navigate(`/expenses/${item.id}/edit`);
  }

  return (
    <div>
      <PageHeader
        title="Pengeluaran"
        description="Pencatatan pengeluaran kas toko."
        action={
          <div className="flex items-center gap-2">
            {table.activeFilters.date_from && table.activeFilters.date_to && (
              <ExpensePdfButton
                dateFrom={table.activeFilters.date_from}
                dateTo={table.activeFilters.date_to}
              />
            )}
            <Button size="sm" onClick={openAdd} disabled={!canCreate}>
              <Plus className="mr-1 h-4 w-4" />
              Tambah
            </Button>
          </div>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        filters={[...workspaceFilter, ...categoryFilters]}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        searchPlaceholder="Cari keterangan..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        dateRange={{
          from: table.activeFilters.date_from ?? "",
          to: table.activeFilters.date_to ?? "",
          onChange: (date_from, date_to) =>
            table.onFiltersChange({ date_from, date_to }),
        }}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <ExpenseRowActions
            item={item}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canDeletePosted={canDeletePosted}
            canApprove={canApprove}
            onEdit={openEdit}
            onComplete={setCompleting}
            onDelete={setDeleting}
            onHistory={(e) => setHistoryExpenseId(e.id)}
          />
        )}
      />

      <EntityActivityLogDialog
        entityName="expense"
        entityId={historyExpenseId}
        open={historyExpenseId !== null}
        onOpenChange={(next) => !next && setHistoryExpenseId(null)}
      />

      <ConfirmDialog
        open={completing !== null}
        onOpenChange={(next) => !next && setCompleting(null)}
        title="Selesaikan pengeluaran?"
        description="Pengeluaran dibukukan. Bila ada barang stok yang dibeli, stok dan harga rata-rata bahan baku ikut diperbarui."
        confirmLabel="Ya, Selesaikan"
        variant="default"
        isConfirming={isCheckingImpact}
        onConfirm={() => {
          if (!completing) return;
          const item = completing;
          setCompleting(null);
          void handleComplete(item);
        }}
      />

      <DeleteReasonDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Hapus Pengeluaran"
        description={
          deleting
            ? `Pengeluaran "${deleting.description}" akan dihapus.`
            : undefined
        }
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />

      <CostImpactDialog
        open={!!pendingImpact}
        impact={pendingImpact?.impact ?? null}
        isSubmitting={isCheckingImpact}
        onConfirm={() => {
          if (pendingImpact) {
            executeComplete(pendingImpact.expenseId, true);
          }
        }}
        onCancel={() => setPendingImpact(null)}
      />
    </div>
  );
}
